import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { AppShell, LocaleToggle, type Locale } from "@kj/design-system";
import { useTranslation } from "react-i18next";
import HomePage from "./pages/HomePage";
import MatchesPage from "./pages/MatchesPage";
import ChatPage from "./pages/ChatPage";
import ProfilePage from "./pages/ProfilePage";
import OnboardingPage from "./pages/OnboardingPage";
import { useSession } from "./store/session";
import type { ReactNode } from "react";

type Active = "home" | "matches" | "chat" | "profile";

function pathToActive(pathname: string): Active {
  if (pathname.startsWith("/matches")) return "matches";
  if (pathname.startsWith("/chat")) return "chat";
  if (pathname.startsWith("/profile")) return "profile";
  return "home";
}

function RequireAuth({ children }: { children: ReactNode }) {
  const user = useSession((s) => s.user);
  const location = useLocation();
  if (!user || !user.isAdult) {
    return (
      <Navigate
        to="/onboarding"
        state={{ from: location.pathname }}
        replace
      />
    );
  }
  return <>{children}</>;
}

export default function App() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const active = pathToActive(location.pathname);
  const user = useSession((s) => s.user);
  const signOut = useSession((s) => s.signOut);

  const onLocale = (l: Locale) => {
    void i18n.changeLanguage(l);
    try {
      localStorage.setItem("kj-locale", l);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = l;
  };

  if (location.pathname === "/onboarding") {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
      </Routes>
    );
  }

  return (
    <AppShell
      active={active}
      onNavigate={(k) =>
        navigate(k === "home" ? "/" : `/${k}`, { replace: false })
      }
      topRight={
        <div className="flex items-center gap-3">
          <LocaleToggle
            value={(i18n.language as Locale) ?? "ko"}
            onChange={onLocale}
          />
          {user ? (
            <button
              type="button"
              onClick={() => {
                signOut();
                navigate("/onboarding", { replace: true });
              }}
              className="text-xs text-fg-muted hover:text-fg underline"
            >
              로그아웃
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/onboarding")}
              className="rounded-full bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white"
            >
              시작하기
            </button>
          )}
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route
          path="/matches"
          element={
            <RequireAuth>
              <MatchesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/chat"
          element={
            <RequireAuth>
              <ChatPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
      </Routes>
    </AppShell>
  );
}
